/**
 * Assessment Engine UI tokens — aligned with TMOS theme CSS variables
 * (see index.css, RiskManagementPage, RoadmapMarketplacePage patterns).
 */

export const AE_PAGE = 'min-h-screen bg-[var(--color-secondary-bg)]';
export const AE_CONTAINER = 'container mx-auto px-2 py-2';
export const AE_INNER = 'p-4';

export const AE_TITLE = 'text-xl font-bold text-[var(--color-surface-text)]';
export const AE_SUBTITLE = 'mt-0.5 text-xs text-[var(--color-secondary-text)]';
export const AE_SECTION_TITLE = 'text-lg font-semibold text-[var(--color-surface-text)]';
/** Hub / dense forms — smaller section headers. */
export const AE_SECTION_TITLE_COMPACT = 'text-sm font-semibold text-[var(--color-surface-text)]';
export const AE_MUTED = 'text-sm text-[var(--color-secondary-text)]';

export const AE_CARD =
  'border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] p-6 shadow-sm';
/** Compact card for short profile / demographics forms — less empty space, feels quicker. */
export const AE_CARD_COMPACT =
  'border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] p-3 sm:p-4 shadow-sm';
export const AE_CARD_LIST =
  'divide-y divide-[var(--color-border)] border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] shadow-sm';

export const AE_LABEL = 'block text-sm font-medium text-[var(--color-surface-text)] mb-1';
export const AE_LABEL_COMPACT = 'block text-xs font-medium text-[var(--color-surface-text)] mb-0.5';

export const AE_INPUT =
  'w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent';
export const AE_INPUT_COMPACT =
  'w-full px-2 py-1.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent';

export const AE_TEXTAREA = `${AE_INPUT} resize-y`;
export const AE_TEXTAREA_COMPACT = `${AE_INPUT_COMPACT} resize-y`;

export const AE_SELECT = AE_INPUT;
export const AE_SELECT_COMPACT = AE_INPUT_COMPACT;

/** Builder inline text — dotted underline instead of a boxed border */
export const AE_EDITABLE_FIELD =
  'border-0 border-b border-dotted border-[var(--color-border)] bg-transparent rounded-none shadow-none px-0 focus:outline-none focus:ring-0 focus:border-[var(--color-accent-bg)]';

export const AE_EDITABLE_INPUT =
  `w-full py-1 text-[var(--color-surface-text)] ${AE_EDITABLE_FIELD}`;

export const AE_EDITABLE_TEXTAREA =
  `${AE_EDITABLE_INPUT} resize-y min-h-[2.5rem] leading-normal w-full`;

export const AE_BTN_PRIMARY =
  'inline-flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] text-sm font-medium rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50 disabled:cursor-not-allowed';

export const AE_BTN_SECONDARY =
  'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-[var(--color-surface-text)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-hover-bg)] disabled:opacity-50';

export const AE_BTN_SUCCESS =
  'inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-[var(--color-success)] text-white hover:opacity-90 disabled:opacity-50';

export const AE_BTN_WARNING =
  'inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-[var(--color-warning)] text-white hover:opacity-90 disabled:opacity-50';

export const AE_BTN_DANGER =
  'inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md text-[var(--color-error)] border border-[var(--color-error)] hover:bg-[var(--color-error)]/10 disabled:opacity-50';

export const AE_BTN_DANGER_SOLID =
  'inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md bg-[var(--color-error)] text-white hover:opacity-90 disabled:opacity-50';

export const AE_LINK =
  'font-medium text-[var(--color-surface-text)] hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]';

export const AE_FILTER_TAB_ACTIVE =
  'px-3 py-1 rounded-md text-sm bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] font-medium';

export const AE_FILTER_TAB =
  'px-3 py-1 rounded-md text-sm text-[var(--color-secondary-text)] hover:bg-[var(--color-hover-bg)] hover:text-[var(--color-surface-text)]';

export const AE_STATUS_DRAFT_TEXT =
  'text-[var(--color-warning)] italic whitespace-nowrap';

export const AE_STATUS_BADGE: Record<string, string> = {
  draft: 'bg-[var(--color-accent-bg)] text-[var(--color-surface-text)]',
  open: 'bg-[var(--color-success)]/15 text-[var(--color-success)]',
  closed: 'bg-[var(--color-error)]/15 text-[var(--color-error)]',
  published: 'bg-[var(--color-success)]/15 text-[var(--color-success)]',
  retired: 'bg-[var(--color-warning)]/15 text-[var(--color-warning)]',
  in_progress: 'bg-[var(--color-info)]/15 text-[var(--color-info)]',
  complete: 'bg-[var(--color-success)]/15 text-[var(--color-success)]',
  incomplete: 'bg-[var(--color-warning)]/15 text-[var(--color-warning)]',
};

export const AE_SPINNER =
  'animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--color-surface-text)]';

/** Scale / multiple-choice option chips on public take flow. */
export const AE_CHOICE_SELECTED =
  'px-3 py-2 rounded border text-sm bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] border-[var(--color-accent-bg)]';

export const AE_CHOICE_DEFAULT =
  'px-3 py-2 rounded border text-sm bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)] hover:bg-[var(--color-hover-bg)]';

export const AE_CHOICE_NA_SELECTED =
  'px-3 py-2 rounded border text-sm bg-[var(--color-secondary-text)] text-[var(--color-surface-bg)] border-[var(--color-secondary-text)]';

export const AE_REQUIRED = 'text-[var(--color-error)]';

/** Gap/radar spider chart — dedicated series colors (contrast on light cards + each other). */
export const AE_CHART_SCORE_COLOR = 'var(--color-chart-series-you)';
export const AE_CHART_AGGREGATE_COLOR = 'var(--color-chart-series-campaign)';
export const AE_CHART_BENCHMARK_COLOR = 'var(--color-chart-series-benchmark)';
export const AE_CHART_FILL_OPACITY = 0.22;
export const AE_CHART_LABEL_CLASS = 'text-xs text-[var(--color-surface-text)]';

export const AE_TEXT_LINK =
  'text-xs font-medium text-[var(--color-info)] hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]';

export const AE_TEXT_DANGER_LINK =
  'text-xs font-medium text-[var(--color-error)] hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--color-error)]';

/** Public respondent shell (no authenticated layout chrome). */
export const AE_ICON_BTN =
  'inline-flex items-center justify-center p-2 rounded-md border border-[var(--color-border)] text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50 disabled:cursor-not-allowed';

export const AE_ICON_BTN_DANGER =
  'inline-flex items-center justify-center p-2 rounded-md border border-[var(--color-error)]/40 text-[var(--color-error)] hover:bg-[var(--color-error)]/10 focus:outline-none focus:ring-2 focus:ring-[var(--color-error)] disabled:opacity-50 disabled:cursor-not-allowed';

export const AE_ICON_BTN_PRIMARY =
  'inline-flex items-center justify-center p-2 rounded-md bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50 disabled:cursor-not-allowed';

export const AE_ICON_BTN_SUCCESS =
  'inline-flex items-center justify-center p-2 rounded-md border border-[var(--color-border)] text-[var(--color-success)] hover:bg-[var(--color-success)]/10 focus:outline-none focus:ring-2 focus:ring-[var(--color-success)] disabled:opacity-50 disabled:cursor-not-allowed';

export const AE_ICON_BTN_WARNING =
  'inline-flex items-center justify-center p-2 rounded-md border border-[var(--color-border)] text-[var(--color-warning)] hover:bg-[var(--color-warning)]/10 focus:outline-none focus:ring-2 focus:ring-[var(--color-warning)] disabled:opacity-50 disabled:cursor-not-allowed';

export const AE_ICON_SIZE = 'h-4 w-4';

/** Template builder entity icons — sections use accent, subsections muted accent, questions info. */
export const AE_BUILDER_SECTION_ICON = 'text-[var(--color-accent-bg)] shrink-0';
export const AE_BUILDER_SUBSECTION_ICON = 'text-[var(--color-secondary-font)] shrink-0';
export const AE_BUILDER_QUESTION_ICON = 'text-[var(--color-info)] shrink-0';

export const AE_BUILDER_SECTION_BADGE =
  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-0.5 text-xs font-semibold bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]';

export const AE_BUILDER_SUBSECTION_BADGE =
  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-0.5 text-xs font-semibold bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)]';

export const AE_BUILDER_QUESTION_LABEL =
  'inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-surface-text)]';

/** Public respondent / shared report shell — full container width like authenticated pages. */
export const AE_PUBLIC_PAGE = 'min-h-screen flex flex-col bg-[var(--color-secondary-bg)]';
export const AE_PUBLIC_MAIN = 'flex-1 container mx-auto w-full px-2 py-2';

/**
 * Assessment response surface. On desktop it uses 75% of the available width,
 * consuming half of the space previously left as equal side margins.
 */
export const AE_ASSESSMENT_CONTENT_WIDTH = 'mx-auto w-full lg:w-3/4';
